import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { EditResourceInstanceComposer } from "@/forms/rema/editResourceInstance/composer"
import { useApiQuery } from "@/api/ApiQueryServiceProvider"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Database, Loader2 } from "lucide-react"
import { useToast } from "@/contexts/ToastContext"

function EditResourceInstancePage() {
  const { instanceNumber } = Route.useParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  // Intentionally fetches the full list then finds client-side. reMa has no
  // getResourceInstanceByNumber endpoint, and getResourceInstances returns the
  // entire table (no server-side pagination), so the record is always present
  // client-side and the list is normally already in cache from the list view.
  // This is NOT the pagination bug fixed on the other domains — switch to a
  // single-record fetch only once the backend adds getResourceInstanceByNumber.
  const { data, isLoading } = useApiQuery("getResourceInstances", {})

  const instance = ((data as any)?.content?.resourceInstances || (data as any)?.resourceInstances || []).find(
    (i: any) => String(i.instanceNumber) === String(instanceNumber)
  )

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-4">
      <Card className="shadow-sm pb-0">
        <CardHeader className="border-b bg-slate-700 text-white [&_p]:text-slate-300">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Database className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl">Resource Instance</CardTitle>
              <CardDescription className="text-base">Update resource instance configuration</CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>
      <Card className="shadow-sm">
        <CardContent className="p-6">
          <EditResourceInstanceComposer
            showHeader={false}
            initialValues={{
              instanceNumber: instance?.instanceNumber,
              namespace: instance?.namespace,
              resourceCategory: instance?.resourceCategory,
              resourceTypeNumber: instance?.resourceTypeNumber,
              parentInstancePaths: instance?.parentInstancePaths ?? [],
              resourceInstanceName: instance?.resourceInstanceName ?? '',
              resourceInstanceDescription: instance?.resourceInstanceDescription ?? '',
            }}
            submitLabel="Save Changes"
            onSuccess={() => navigate({ to: '/rema/resource-instances' })}
            onError={(error) => toast({ title: 'Error', description: `Failed to update instance: ${error.message}`, type: 'error' })}
            onCancel={() => navigate({ to: '/rema/resource-instances' })}
          />
        </CardContent>
      </Card>
    </div>
  )
}

export const Route = createFileRoute("/rema/resource-instances/$instanceNumber_/edit")({
  staticData: {
    title: 'Edit Resource Instance',
    isSecured: true,
  },
  component: EditResourceInstancePage,
})
